import {Injectable, Injector} from '@angular/core';
import {AuthenticateWebSocketHandlerService} from './websocket/authenticate-web-socket-handler.service';
import {WebSocketService} from './websocket/web-socket.service';
import {NotificationWebSocketHandlerService} from './websocket/notification-web-socket-handler.service';
import {BehaviorSubject} from 'rxjs/Rx';
import {ProcessRequest} from '../../models/process-request.model';
import {ConfigurationService} from './configuration/configuration.service';
import {RestApiService} from '../../../shared/services/rest-api.service';
import {v4 as uuid} from 'uuid';
import {NotificationStorageService} from '../../../shared/services/notification-storage.service';
import {MetricsService, RequestMetricName} from '../../../shared/services/metrics.service';
import {RequestMetricResult} from '../../../shared/components/models/metrics/request-metric-result.enum';
import {MessageType} from '../../../shared/models/request-offer/request-offer.enum';
import {ProcessService} from '../process.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {LanguageService} from '../../../shared/services/language.service';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {AppMode} from '../../../shared/models/app-mode.enum';
import {ApiParamsService} from '../api-params.service';
import {ApiRequestKey} from '../../../shared/enums/api-request-key.enum';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  notificationSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private readonly CLASSNAME = 'NotificationService';
  private readonly API_REQUEST_KEY: ApiRequestKey = ApiRequestKey.NOTIFICATION;
  private subscriptions = [];

  constructor(private injector: Injector,
              private configuration: ConfigurationService,
              private api: RestApiService,
              private notificationStorageService: NotificationStorageService,
              private metricsService: MetricsService,
              private processService: ProcessService,
              private appStateService: ApplicationStateService,
              private route: ActivatedRoute,
              private router: Router,
              private languageService: LanguageService,
              private apiParamsService: ApiParamsService) {
  }

  initNotification() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.initWebSocketNotificationService();
    this.appStateService.mode = AppMode.NOTIFICATIONS;
  }

  redirectToNotification(path: NotificationPath, queryParams?: any): void {
    const uri = ['notifications', path];
    const params = queryParams ? queryParams : {};
    this.router.navigate(uri, {
      queryParams: params,
      relativeTo: this.route
    });
  }

  emitNotification(notification: any): void {
    this.notificationSubject.next(notification);
  }

  sendNotification(msgType?: MessageType, reqBody?: any) {
    let uri = this.configuration.uiConfig.sendNotificationEndpoint;

    const requestId = uuid();
    const messageType = msgType ? msgType : MessageType.WS_READY;
    const requestBody = reqBody ? reqBody : {};

    let body: ProcessRequest = {
      header: {
        languageCode: this.languageService.getDefaultLanguage().toUpperCase(),
        requestId: requestId,
        responseId: requestId,
        messageType: messageType,
        requestDate: new Date(),
        responseDate: new Date(),
        processContext: {
          processId: this.processService.processId,
          businessParameters: this.apiParamsService.createBusinessParams({
            requestKey: this.API_REQUEST_KEY
          })
        },
        technicalParameters: this.apiParamsService.createTechnicalParams({
          requestKey: this.API_REQUEST_KEY
        })
      },
      body: requestBody
    };

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'sendNotification'
    };

    this.metricsService.sendRequestMetric(<RequestMetricName>{
      ...metricName,
      type: RequestMetricResult.SEND
    }, body);
    return this.api.post(uri, body)
      .then(() => {
        this.notificationStorageService.putNotificationRequest();
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.SUCCESS
        });
        return Promise.resolve()
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
      });
  }

  sendNotificationMetric(notification: any, fromWebsocket: boolean): void {
    const name = fromWebsocket ? CustomMetricsName.WEBSOCKET_NOTIFICATION : CustomMetricsName.SESSION_NOTIFICATION;
    const value = JSON.stringify(notification);
    this.metricsService.sendSimpleMetric(name, MetricsType.NOTIFICATION, value);
  }

  getNotificationPath(status: number): NotificationPath {
    switch (status) {
      case 200:
        return NotificationPath.SUCCESS;
      case 402:
        return NotificationPath.CANCEL;
      case 406:
        return NotificationPath.AUTHORIZATION;
      case 407:
        return NotificationPath.CANCEL;
      case 409:
        return NotificationPath.ACCEPT;
      default:
        console.error('Unsupported payment status', status);
        return null;
    }
  }

  private initWebSocketNotificationService() {
    let authenticateWebSocketHandlerService = this.injector.get(AuthenticateWebSocketHandlerService);
    let notificationWebSocketHandlerService: NotificationWebSocketHandlerService = this.injector.get(NotificationWebSocketHandlerService);
    let webSocketService = this.injector.get(WebSocketService);

    let notificationSubscription = notificationWebSocketHandlerService.notificationObservable
      .subscribe(notification => {
        if (isNotNullOrUndefined(notification)) {
          this.sendNotificationMetric(notification, true);
        }
        this.notificationSubject.next(notification);
      });

    this.subscriptions.push(notificationSubscription);

    let authenticationSubscription = authenticateWebSocketHandlerService.authObservable.take(1).subscribe(() => {
      // wykona się w momęcie gdy powiedzie się authenyfikacja websocket
      this.sendNotification();
    });
    this.subscriptions.push(authenticationSubscription);
    webSocketService.connect();
  }

}

export enum NotificationPath {
  SUCCESS = 'success',
  CANCEL = 'cancel',
  ACCEPT = 'accept',
  AUTHORIZATION = 'authorization'
}
